import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

const CATEGORIES = ['Maintenance', 'Cleaning', 'Supplies', 'Insurance', 'HOA', 'Utilities', 'Other'];

export default function ExpenseModal({ expense, properties, onSaved, onClose }) {
  const [propertyId, setPropertyId] = useState('');
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('Other');
  const [expenseDate, setExpenseDate] = useState('');
  const [vendor, setVendor] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (expense) {
      setPropertyId(expense.property_id || '');
      setTitle(expense.title || '');
      setAmount(expense.amount || '');
      setCategory(expense.category || 'Other');
      setExpenseDate(expense.expense_date || '');
      setVendor(expense.vendor || '');
      setNotes(expense.notes || '');
    } else {
      if (properties.length === 1) setPropertyId(properties[0].id);
      setExpenseDate(new Date().toISOString().split('T')[0]);
    }
  }, [expense, properties]);

  const handleSave = async () => {
    if (!title.trim()) { setError('Title is required.'); return; }
    if (!amount) { setError('Amount is required.'); return; }
    if (!expenseDate) { setError('Date is required.'); return; }
    setLoading(true);
    setError('');

    const payload = {
      property_id: propertyId || null,
      title: title.trim(),
      amount: parseFloat(amount),
      category,
      expense_date: expenseDate,
      vendor: vendor.trim() || null,
      notes: notes.trim() || null,
    };

    let saved;
    if (expense) {
      const { data, error: err } = await supabase
        .from('expenses')
        .update(payload)
        .eq('id', expense.id)
        .select('*, properties(name)')
        .single();
      if (err) { setError(err.message); setLoading(false); return; }
      saved = data;
    } else {
      const { data, error: err } = await supabase
        .from('expenses')
        .insert(payload)
        .select('*, properties(name)')
        .single();
      if (err) { setError(err.message); setLoading(false); return; }
      saved = data;
    }

    setLoading(false);
    onSaved(saved, !expense);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{expense ? 'Edit Expense' : 'Add Expense'}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {error && <div className="error-msg">{error}</div>}

          <div className="form-group">
            <label>Property</label>
            <select value={propertyId} onChange={e => setPropertyId(e.target.value)}>
              <option value="">-- Select --</option>
              {properties.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Title *</label>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Monthly cleaning" />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Amount ($) *</label>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" min="0" />
            </div>
            <div className="form-group">
              <label>Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Date *</label>
              <input type="date" value={expenseDate} onChange={e => setExpenseDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Vendor</label>
              <input value={vendor} onChange={e => setVendor(e.target.value)} placeholder="e.g. Beach Clean Co" />
            </div>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any additional details..." />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : expense ? 'Save Changes' : 'Add Expense'}
          </button>
        </div>
      </div>
    </div>
  );
}
